import { action, computed, observable } from "mobx";
import NestStore from "~/stores/NestStore";
import SubscriptionRepository from "~/repository/Subscription/SubscriptionRepository";
import Subscription from "~/models/Subscription/Subscription";

class SubscriptionStore extends NestStore {
  /**
   * @property {Array}
   */
  @observable subscriptions = [];

  /**
   * @property {Subscription|null}
   */
  @observable subscription = null;

  /**
   * @property {number|null}
   */
  subscriptionId = null;

  subscriptionRepository = null;

  constructor() {
    super();

    this.subscriptionRepository = new SubscriptionRepository();

    this.setDefaultSearchParams();
  }

  @action
  load() {
    this.isLoading = true;

    return Promise.all([this.getOne()]).finally(() => {
      this.isLoading = false;
    });
  }

  @action
  loadIndex() {
    this.isLoading = true;

    return Promise.all([this.getAll()]).finally(() => {
      this.isLoading = false;
    });
  }

  /**
   * Get all.
   */
  @action
  getAll() {
    const params = {
      offset: this.offset,
      limit: this.pagination.itemsPerPage,
    };

    if (this.searchParams.providerId !== "") {
      params.providerId = this.searchParams.providerId;
    }

    if (this.searchParams.customerId !== "") {
      params.customerId = this.searchParams.customerId;
    }

    if (this.searchParams.status !== "") {
      params.status = this.searchParams.status;
    }

    if (this.searchParams.notAsAdminProvider) {
      params.notAsAdminProvider = 1;
    }

    return this.subscriptionRepository.fetch(params).then(response => {
      this.processEntities(response.data.subscriptions);
      this.processPagination(response.data.pagination);
    });
  }

  @action
  processEntities(subscriptions = []) {
    this.subscriptions = [];

    subscriptions.forEach(subscriptionArray => {
      const subscription = new Subscription(subscriptionArray);

      this.subscriptions.push(subscription);
    });
  }

  /**
   * Change the pagination page and fetch the Photo Editing Jobs.
   */
  @action
  changePage(page) {
    this.pagination.currentPage = page;

    return this.loadIndex();
  }

  /**
   * Change the pagination page and fetch the Photo Editing Jobs.
   */
  @action
  search() {
    this.pagination.currentPage = 1;

    return this.loadIndex();
  }

  @action
  getOne() {
    return this.subscriptionRepository.fetchOne(this.subscriptionId).then(response => {
      this.subscription = new Subscription(response.data.subscription);
    });
  }

  @action
  create(data) {
    this.isSaving = true;

    return this.subscriptionRepository
      .create(data)
      .then(response => {
        this.subscription = new Subscription(response.data.subscription);
        this.isSaving = false;
      })
      .catch(e => {
        this.isSaving = false;
        this.setErrorMessages(e);
        throw e;
      });
  }

  @action
  update(data) {
    this.isSaving = true;

    return this.subscriptionRepository
      .update(this.subscriptionId, data)
      .then(response => {
        this.subscription = new Subscription(response.data.subscription);
        this.isSaving = false;
      })
      .catch(e => {
        this.isSaving = false;
        this.setErrorMessages(e);
        throw e;
      });
  }

  @computed
  get activeSubscriptions() {
    return this.subscriptions.filter(subscription => subscription.canceledDts === null);
  }

  @computed
  get pastSubscriptions() {
    return this.subscriptions.filter(subscription => subscription.canceledDts !== null);
  }

  /**
   * Set default search params.
   */
  @action
  setDefaultSearchParams() {
    this.searchParams.providerId = "";
    this.searchParams.customerId = "";
    this.searchParams.status = "";
    this.searchParams.notAsAdminProvider = false;
  }
}

export default SubscriptionStore;
